import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export interface StripeCheckoutParams {
  priceId: string;
  userId: string;
  email: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface StripePlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  studentLimit: number;
  features: string[];
  popular?: boolean;
  trialDays?: number;
}

/**
 * ✅ Stripe Plan Configuration (aligned with planLimits.js)
 *
 * IMPORTANT: These limits must match planLimits.js getPlanLimits()
 * - Monthly: 50 students, unlimited AI, unlimited quizzes
 * - Yearly: 50 students, unlimited AI, unlimited quizzes
 * - Trial (not here): 10 students, 50 AI questions total, 3 quiz saves
 *
 * ✅ P1-5 FIX: Price IDs now use environment variables with safe fallbacks
 */

// Get price IDs from environment variables (set via .env files)
// ⚠️ TEMPORARILY IN TEST MODE - Switch back to live mode after testing
const MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_MONTHLY || 'price_1T5PZpETQlQ5pS35O1Mnaa9G';
const YEARLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_YEARLY || 'price_1T5PZpETQlQ5pS35mUlxWdrm';

// ⚠️ Warn if using fallback test price IDs in production
if (import.meta.env.PROD && !import.meta.env.VITE_STRIPE_PRICE_MONTHLY) {
  console.warn('⚠️ STRIPE WARNING: Using fallback test price IDs. Set VITE_STRIPE_PRICE_MONTHLY in .env.production');
}

export const STRIPE_PLANS: StripePlan[] = [
  {
    id: MONTHLY_PRICE_ID,
    name: 'Monthly Plan',
    price: 999, // $9.99 in cents
    interval: 'month',
    studentLimit: 50,  // ✅ Aligned with planLimits.js
    features: [
      'Up to 50 students',
      '2,000 AI questions/month',  // ✅ PR5: Exact monthly cap (matches backend)
      'Unlimited quiz library saves',
      'Advanced anti-cheat monitoring',
      'Real-time violation alerts',
      'Teacher-controlled exam resumption',
      'Detailed analytics',
      'CSV exports',
      'Email support'
    ]
  },
  {
    id: YEARLY_PRICE_ID,
    name: 'Yearly Plan',
    price: 9999, // $99.99 in cents
    interval: 'year',
    studentLimit: 50,  // ✅ Aligned with planLimits.js
    popular: true,
    features: [
      'Up to 50 students',
      '5,000 AI questions/month',  // ✅ PR5: Exact monthly cap (matches backend)
      'Unlimited quiz library saves',
      'Advanced anti-cheat monitoring',
      'Real-time violation alerts',
      'Teacher-controlled exam resumption',
      'Detailed analytics',
      'CSV exports',
      'Priority email support',
      '💰 Save $19.89/year!'
    ]
  }
];

/**
 * Create Stripe checkout session
 */
export async function createCheckoutSession(params: StripeCheckoutParams): Promise<string> {
  try {
    const createCheckoutSession = httpsCallable(functions, 'createStripeCheckoutSession');
    
    const result = await createCheckoutSession({
      priceId: params.priceId,
      userId: params.userId,
      email: params.email,
      successUrl: params.successUrl || `${window.location.origin}/billing?success=true`,
      cancelUrl: params.cancelUrl || `${window.location.origin}/billing?canceled=true`
    });

    if (!result.data?.sessionUrl) {
      throw new Error('No checkout session URL returned');
    }

    return result.data.sessionUrl;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw new Error('Failed to create checkout session. Please try again.');
  }
}

/**
 * Create customer portal session for managing subscription
 */
export async function createPortalSession(userId: string): Promise<string> {
  try {
    const createPortalSession = httpsCallable(functions, 'createStripePortalSession');
    
    const result = await createPortalSession({
      userId,
      returnUrl: `${window.location.origin}/billing`
    });

    // ✅ PR1: Accept both portalUrl and url for backward compatibility
    const portalUrl = result.data?.portalUrl || result.data?.url;

    if (!portalUrl) {
      throw new Error('No portal session URL returned');
    }

    return portalUrl;
  } catch (error) {
    console.error('Error creating portal session:', error);
    throw new Error('Failed to create portal session. Please try again.');
  }
}

/**
 * Get plan by ID
 */
export function getPlanById(priceId: string): StripePlan | null {
  return STRIPE_PLANS.find(plan => plan.id === priceId) || null;
}

/**
 * Format price for display
 */
export function formatPrice(priceInCents: number): string {
  return `$${(priceInCents / 100).toFixed(2)}`;
}

/**
 * Calculate savings for yearly plan
 */
export function calculateYearlySavings(): { savings: number; percentage: number } {
  const monthlyPlan = STRIPE_PLANS.find(p => p.interval === 'month');
  const yearlyPlan = STRIPE_PLANS.find(p => p.interval === 'year');
  
  if (!monthlyPlan || !yearlyPlan) {
    return { savings: 0, percentage: 0 };
  }
  
  const monthlyYearlyCost = monthlyPlan.price * 12;
  const savings = monthlyYearlyCost - yearlyPlan.price;
  const percentage = Math.round((savings / monthlyYearlyCost) * 100);
  
  return { savings, percentage };
}